import React, { useMemo } from "react";
import DownArrow from "../../../../assets/icons/DownArrow";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const formatRangeLabel = (startDate, endDate) => {
  if (!startDate) return "";
  const options = { day: "2-digit", month: "short", year: "numeric" };
  const startLabel = startDate.toLocaleDateString("en-GB", options);
  if (!endDate) return startLabel;
  const endLabel = endDate.toLocaleDateString("en-GB", options);
  return `${startLabel} - ${endLabel}`;
};

function FinancialDateRangePicker({
  currentMonth,
  startDate,
  endDate,
  onMonthChange,
  onDateClick,
  onApply,
  onClear,
  onClose,
}) {
  const days = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const values = [];
    for (let i = 0; i < startingDayOfWeek; i += 1) {
      values.push(null);
    }
    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      values.push(new Date(year, month, day));
    }
    return values;
  }, [currentMonth]);

  const isDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date > today;
  };

  const isSelectedStart = (date) => startDate && date.getTime() === startDate.getTime();
  const isSelectedEnd = (date) => endDate && date.getTime() === endDate.getTime();
  const isInRange = (date) => startDate && endDate && date > startDate && date < endDate;

  return (
    <div className="financial-date-picker-backdrop" onClick={onClose}>
      <div className="financial-date-picker" onClick={(event) => event.stopPropagation()}>
        <div className="financial-date-picker-header">
          <button type="button" className="financial-date-picker-nav-btn" onClick={() => onMonthChange(-1)}>
            <span className="financial-date-picker-nav-icon rotate-left"><DownArrow /></span>
          </button>
          <h3 className="financial-date-picker-month-year">
            {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </h3>
          <button type="button" className="financial-date-picker-nav-btn" onClick={() => onMonthChange(1)}>
            <span className="financial-date-picker-nav-icon rotate-right"><DownArrow /></span>
          </button>
        </div>

        <div className="financial-date-picker-weekdays">
          {WEEKDAYS.map((weekday) => (
            <div key={weekday} className="financial-date-picker-weekday">{weekday}</div>
          ))}
        </div>

        <div className="financial-date-picker-days">
          {days.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="financial-date-picker-day empty" />;
            }

            const disabled = isDisabled(day);
            const selectedStart = isSelectedStart(day);
            const selectedEnd = isSelectedEnd(day);
            const inRange = isInRange(day);
            const selectedSingle = selectedStart && !endDate;

            const className = [
              "financial-date-picker-day",
              disabled ? "disabled" : "",
              selectedSingle ? "selected-single" : "",
              selectedStart && endDate ? "selected-start" : "",
              selectedEnd ? "selected-end" : "",
              inRange ? "in-range" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={day.toISOString()}
                type="button"
                className={className}
                onClick={() => onDateClick(normalizeDate(day))}
                disabled={disabled}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>

        <div className="financial-date-picker-footer">
          <button type="button" className="financial-date-picker-clear" onClick={onClear}>
            Clear
          </button>
          <button
            type="button"
            className="financial-date-picker-apply"
            onClick={onApply}
            disabled={!startDate || !endDate}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

export { formatRangeLabel };
export default FinancialDateRangePicker;